/** Client for the business modules that replaced Odoo. */

import { ApiError, getAccessToken } from './api'

const BASE = '/api/v1'

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body) headers.set('Content-Type', 'application/json')

  const response = await fetch(`${BASE}${path}`, {
    ...init, headers, credentials: 'include',
  })
  if (response.status === 204) return undefined as T

  const text = await response.text()
  const body = text ? JSON.parse(text) : null
  if (!response.ok) {
    // The server writes business-rule refusals for humans, so `title` is shown
    // to the user verbatim rather than replaced with a generic message.
    throw new ApiError(
      response.status,
      body?.title ?? `Request failed (${response.status})`,
      body,
    )
  }
  return body as T
}

// ---------------------------------------------------------------- types ---

export interface Company {
  id: string
  name: string
  gstin: string | null
  state_code: string | null
  state_name: string | null
  gst_configured: boolean
  legal_name: string | null
  address: string | null
  city: string | null
  state: string | null
  pincode: string | null
  phone: string | null
  email: string | null
  website: string | null
  pan: string | null
  bank_name: string | null
  bank_account: string | null
  bank_ifsc: string | null
  upi_id: string | null
  invoice_terms: string | null
  logo_url: string | null
}

export interface Contact {
  id: string
  name: string
  is_customer: boolean
  is_vendor: boolean
  gstin: string | null
  pan: string | null
  state_code: string | null
  state_name: string | null
  email: string | null
  phone: string | null
  billing_address: string | null
  shipping_address: string | null
  city: string | null
  state: string | null
  pincode: string | null
  payment_terms_days: number
  notes: string | null
  is_active: boolean
}

export interface Product {
  id: string
  sku: string | null
  name: string
  description: string | null
  kind: 'goods' | 'service'
  hsn_sac: string | null
  unit: string
  sale_price: number | null
  purchase_price: number | null
  gst_rate: number
  track_stock: boolean
  stock_on_hand: number
  is_active: boolean
}

export interface SalesLine {
  line_no: number
  product_id: string | null
  description: string
  hsn_sac: string | null
  unit: string
  quantity: number
  rate: number
  discount_pct: number
  taxable_value: number
  gst_rate: number
  cgst: number
  sgst: number
  igst: number
  line_total: number
}

export interface SalesDoc {
  id: string
  doc_type: 'quotation' | 'sales_order' | 'invoice' | 'credit_note'
  number: string
  status: string
  payment_status: string
  contact: { id: string; name: string; gstin: string | null } | null
  contact_id: string
  issue_date: string | null
  valid_until: string | null
  due_date: string | null
  place_of_supply: string | null
  place_of_supply_name: string | null
  is_interstate: boolean
  subtotal: number
  cgst: number
  sgst: number
  igst: number
  round_off: number
  total: number
  amount_paid: number
  balance_due: number
  amount_in_words: string
  notes: string | null
  terms: string | null
  reference: string | null
  lines: SalesLine[]
  timeline?: TimelineEntry[]
}

export interface TimelineEntry {
  id: string
  body: string
  kind: string
  author_id: string | null
  created_at: string | null
}

export interface Stage {
  id: string
  name: string
  sort: number
  probability: number
  is_won: boolean
  is_lost: boolean
}

export interface Lead {
  id: string
  title: string
  status: string
  stage: { id: string; name: string } | null
  contact: { id: string; name: string } | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  expected_value: number | null
  probability: number
  expected_close: string | null
  source: string | null
  notes: string | null
  created_at: string | null
}

export interface PipelineColumn {
  stage: Stage
  count: number
  value: number
  weighted_value: number
  leads: Lead[]
}

export interface Dashboard {
  revenue_mtd: number
  open_quotations: { count: number; value: number }
  receivables_outstanding: number
  payables_outstanding: number
  open_leads: { count: number; value: number }
  documents_needing_review: number
  customers: number
  vendors: number
}

export interface AgingReport {
  as_of: string
  total_outstanding: number
  buckets: Array<{ label: string; amount: number }>
  by_contact?: Array<{ contact_id: string; name: string; outstanding: number; invoices: number; days_overdue: number }>
  by_vendor?: Array<{ vendor_id: string; name: string; outstanding: number; bills: number; days_overdue: number }>
}

export interface GstReport {
  period: { start: string; end: string }
  output_tax_by_rate: Array<{
    gst_rate: number; taxable_value: number
    cgst: number; sgst: number; igst: number; total_tax: number
  }>
  output_tax_total: number
  input_tax_total: number
  input_bill_count: number
  net_payable: number
  note: string
}

export interface LineInput {
  product_id?: string | null
  description?: string | null
  hsn_sac?: string | null
  unit?: string | null
  quantity: number
  rate?: number | null
  discount_pct?: number
  gst_rate?: number | null
}

export type SalesKind = 'quotations' | 'sales-orders' | 'invoices'

// ----------------------------------------------------------------- api ----

export const biz = {
  company: () => call<Company>('/company'),
  saveCompany: (body: Partial<Company> & { name: string }) =>
    call<Company>('/company', { method: 'PUT', body: JSON.stringify(body) }),

  contacts: (params: { q?: string; role?: 'customer' | 'vendor' } = {}) => {
    const s = new URLSearchParams()
    if (params.q) s.set('q', params.q)
    if (params.role) s.set('role', params.role)
    return call<{ items: Contact[] }>(`/contacts${s.toString() ? `?${s}` : ''}`)
  },
  contact: (id: string) =>
    call<Contact & { outstanding: number; documents: SalesDoc[]; timeline: TimelineEntry[] }>(
      `/contacts/${id}`),
  createContact: (body: Partial<Contact> & { name: string }) =>
    call<Contact>('/contacts', { method: 'POST', body: JSON.stringify(body) }),
  updateContact: (id: string, body: Partial<Contact> & { name: string }) =>
    call<Contact>(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  products: (q?: string) =>
    call<{ items: Product[] }>(`/products${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  createProduct: (body: Partial<Product> & { name: string }) =>
    call<Product>('/products', { method: 'POST', body: JSON.stringify(body) }),
  updateProduct: (id: string, body: Partial<Product> & { name: string }) =>
    call<Product>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  salesDocs: (kind: SalesKind, status?: string) =>
    call<{ items: SalesDoc[]; next_number: string }>(
      `/sales/${kind}${status ? `?status=${status}` : ''}`),
  salesDoc: (id: string) => call<SalesDoc>(`/sales/doc/${id}`),
  createSalesDoc: (kind: SalesKind, body: {
    contact_id: string; lines: LineInput[]; notes?: string | null
    terms?: string | null; reference?: string | null; lead_id?: string | null
  }) => call<SalesDoc>(`/sales/${kind}`, { method: 'POST', body: JSON.stringify(body) }),
  setStatus: (id: string, status: string) =>
    call<SalesDoc>(`/sales/doc/${id}/status`, {
      method: 'POST', body: JSON.stringify({ status }),
    }),
  convert: (id: string, to: 'sales_order' | 'invoice') =>
    call<SalesDoc>(`/sales/doc/${id}/convert`, {
      method: 'POST', body: JSON.stringify({ to }),
    }),
  updateLines: (id: string, lines: LineInput[]) =>
    call<SalesDoc>(`/sales/doc/${id}/lines`, {
      method: 'PUT', body: JSON.stringify(lines),
    }),

  payments: (direction?: 'in' | 'out') =>
    call<{ items: Array<{
      id: string; direction: string; amount: number; method: string
      reference: string | null; paid_on: string | null
      contact_name: string | null; allocated: number; unallocated: number
    }> }>(`/payments${direction ? `?direction=${direction}` : ''}`),
  recordPayment: (body: {
    direction: 'in' | 'out'; amount: number; contact_id?: string | null
    method?: string; reference?: string | null; paid_on?: string | null
    allocations?: Array<{ target_type: string; target_id: string; amount: number }>
  }) => call<{ id: string }>('/payments', { method: 'POST', body: JSON.stringify(body) }),

  pipeline: (mine = false) =>
    call<{ stages: PipelineColumn[] }>(`/crm/pipeline${mine ? '?mine=true' : ''}`),
  createLead: (body: Partial<Lead> & { title: string }) =>
    call<Lead>('/crm/leads', { method: 'POST', body: JSON.stringify(body) }),
  lead: (id: string) =>
    call<Lead & { timeline: TimelineEntry[] }>(`/crm/leads/${id}`),
  moveLead: (id: string, stage_id: string) =>
    call<Lead>(`/crm/leads/${id}/stage`, {
      method: 'POST', body: JSON.stringify({ stage_id }),
    }),

  addNote: (subject_type: string, subject_id: string, body: string) =>
    call<{ ok: boolean }>('/notes', {
      method: 'POST', body: JSON.stringify({ subject_type, subject_id, body }),
    }),

  dashboard: () => call<Dashboard>('/reports/dashboard'),
  receivables: () => call<AgingReport>('/reports/receivables'),
  payables: () => call<AgingReport>('/reports/payables'),
  gst: () => call<GstReport>('/reports/gst'),
  salesReport: (months = 6) =>
    call<{
      monthly: Array<{ month: string; invoices: number; revenue: number }>
      document_counts: Record<string, number>
      quotation_conversion_rate: number
    }>(`/reports/sales?months=${months}`),
}
